import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import { isAuthCallbackUrl, markHandled, parseAuthCallback } from '../lib/authHandoff';
import { takeNativeFacebookIntent } from '../lib/facebookAuth';
import { clearAnonStory, setClaimedStoryId } from '../lib/anonStory';
import { markFreshAccount } from '../lib/onboarding';
import { isNativeApp } from '../lib/platform';

const ERROR_KEYS: Record<string, string> = {
  facebook_failed: 'auth.facebookFailed', facebook_invalid_token: 'auth.facebookFailed', facebook_no_email: 'auth.facebookNoEmail',
  state_mismatch: 'auth.facebookFailed', handoff_invalid: 'auth.handoffFailed',
};

/** Finishes the native system-browser login (2026-09-18): listens for snugtale://auth/callback, redeems the
 * one-time code with the PKCE verifier and logs the app in. Mounted inside AuthProvider; renders nothing. */
export default function AuthHandoffListener() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginWithTokens } = useAuth();
  useEffect(() => {
    if (!isNativeApp()) return;
    let handle: { remove: () => Promise<void> } | undefined;
    let cancelled = false;
    const finish = async (url: string) => {
      if (!markHandled(url)) return;
      try { const { Browser } = await import('@capacitor/browser'); await Browser.close(); } catch { /* Android: no-op */ }
      const p = parseAuthCallback(url);
      const intent = await takeNativeFacebookIntent();
      if (p.error || !p.code || !intent) {
        if (p.error !== 'access_denied') toast.error(t(ERROR_KEYS[p.error || ''] || 'auth.facebookFailed'));
        navigate('/login', { replace: true });
        return;
      }
      try {
        const r = await authApi.handoffExchange(p.code, intent.verifier);
        await loginWithTokens(r.data.access_token, r.data.refresh_token);
        const claimed = r.data.claimed_story_id || p.claimed;
        if (claimed) { clearAnonStory(); setClaimedStoryId(claimed); }
        if (r.data.is_new_user || p.isNew) { toast.success(t('auth.welcomeNew')); if (intent.next === '/') markFreshAccount(); }
        navigate(intent.next, { replace: true });
      } catch {
        toast.error(t('auth.handoffFailed'));
        navigate('/login', { replace: true });
      }
    };
    import('@capacitor/app')
      .then(({ App: CapApp }) => CapApp.addListener('appUrlOpen', ({ url }) => { if (isAuthCallbackUrl(url)) void finish(url); }))
      .then((h) => { if (cancelled) h.remove(); else handle = h; })
      .catch(() => { /* plugin unavailable */ });
    return () => { cancelled = true; handle?.remove(); };
  }, [loginWithTokens, navigate, t]);
  return null;
}
